import { ProductDescriptors } from "@/components/ui/ProductDescriptors";
import { formatCents } from "@/lib/utils";
import { AcceptAction } from "@/components/ui/AcceptAction";
import { DynamicHtmlElement } from "@/components/ui/DynamicHtmlElement";
import Image from "next/image";
import { QuoteResponse } from "@vertical-insure/shared";
import { useFormContext, Controller } from "react-hook-form";
import { useContext } from "react";
import { ProductFormContext } from "@/lib/product-form-context";
import { RiArrowDownWideLine, RiArrowUpWideLine } from "@remixicon/react";

interface PetSingleVidLayoutProps {
  option: QuoteResponse;
  expanded: boolean;
  onToggleExpand: () => void;
  badgeText: string;
  showBadge: boolean;
}

export const PetSingleVidLayout = ({ option, expanded, onToggleExpand, badgeText, showBadge }: PetSingleVidLayoutProps) => {
  const form = useFormContext();
  const productFormContext = useContext(ProductFormContext);

  return (
    <Controller 
      control={form.control} 
      name={`products[${productFormContext}]`} 
      render={({field}) => {
        const isSelected = field.value?.accepted_quotes?.includes(option.quote_id);
        return (
          <div className={`border rounded-xl shadow-sm mb-6 overflow-hidden transition-colors duration-200 ${
            isSelected ? 'bg-primary/10' : 'bg-white'
          }`}>
            {/* Badge */}
            <div className="flex items-center gap-2 px-4 pt-4">
              {showBadge && (
                <span className="inline-block text-xs font-bold px-3 py-1 rounded-full bg-secondary text-white">{badgeText}</span>
              )}
            </div>
            <div className="flex flex-col gap-1 px-4 pt-2">
              <div className="flex items-center">
                <div className="w-[200px]">
                  <DynamicHtmlElement 
                    html={option.product.promotional_header}
                    className="text-lg font-bold whitespace-nowrap"
                  />
                </div>
                <div className="flex-1 text-right">
                  <span className="text-xs text-gray-500">backed by {option.product.carrier_name}</span>
                </div>
              </div>
            </div>
            {option.product.promotional_description && (
              <DynamicHtmlElement
                html={option.product.promotional_description}
                className="px-4 pt-2 pb-1 text-sm text-gray-800"
              />
            )}
            <div className="flex items-center justify-between px-4 py-2">
              <div className="text-2xl font-bold text-primary">
                {option.subscription_premium?.payment_amount ? (
                  <>
                    {formatCents(option.subscription_premium.payment_amount, option.currency ?? 'USD')}<span className="text-base font-normal text-gray-700">/month</span>
                  </>
                ) : (
                  <span className="text-base text-gray-500">N/A</span>
                )}
              </div>
              <div className="flex-shrink-0">
                <AcceptAction type="button" quoteIds={[option.quote_id]} className="px-6 py-2 text-sm font-semibold">
                  {isSelected ? "SELECTED" : "SELECT"}
                </AcceptAction>
              </div>
            </div>
            <div className="flex justify-center">
              <button
                className="flex items-center justify-center w-8 h-8 text-primary focus:outline-none"
                onClick={onToggleExpand}
                aria-expanded={expanded}
                aria-label="Toggle details"
              >
                {expanded ? (
                  <RiArrowUpWideLine className="w-6 h-6" />
                ) : (
                  <RiArrowDownWideLine className="w-6 h-6" />
                )}
              </button>
            </div>
            {/* Collapsible details */}
            {expanded && (
              <div className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <DynamicHtmlElement 
                    html={option.product.promotional_header || 'Illness & Injury'}
                    className="font-semibold"
                  /> by
                  {option.product.carrier_logo_url && (
                    <Image
                      src={option.product.carrier_logo_url}
                      alt="Carrier logo"
                      width={80}
                      height={32}
                      className="h-8 w-auto max-w-[120px] object-contain"
                      unoptimized
                    />
                  )}
                </div>
                <ProductDescriptors descriptors={option.product.descriptors ?? option.product.perils} />
              </div>
            )}
          </div>
        );
      }}
    />
  );
}; 